/**
 * Base Template Black - Alternative black version
 * Original dark design preserved as an alternative to the white BaseTemplate
 */

import {
  BRAND_NAME,
  BRAND_PERSON_NAME,
} from "@/lib/brandConfig";

import { useLanguage } from "@/hooks/useLanguage";
import { useTranslation } from "react-i18next";
// Amplitude tracking removed to prevent React error #310
import {
  trackTemplateUsageGA,
} from "@/lib/analytics";
import {
  applyInstagramBrowserCSS,
  getInstagramOptimizations,
} from "@/lib/instagramBrowserDetection";
import { trackBaseTemplateClick } from "@/lib/baseTemplateAnalytics";
import { usePhotoImpressions } from "@/hooks/usePhotoImpressions";
import { trackPhotoOpen, cloudinaryPublicIdFromUrl } from "@/lib/photoTracking";
import {
  buildHeroCtaFlagsSnapshot,
  buildHeroCtaSessionContext,
  installHeroCtaEngagementWatch,
  postHeroCtaSlackFunnel,
  resolveActiveHeroCtaType,
  uninstallHeroCtaEngagementWatch,
} from "@/lib/heroCtaFunnel";
import {
  isExternalHeroCtaHref,
  resolveBirthdayHeroHref,
  resolveCheckoutHeroHref,
  resolveCommunityHeroHref,
  resolveHeroCtaAriaLabel,
  resolveHeroCtaDisplayLabel,
  resolveHeroCtaHrefForKind,
} from "@/lib/heroCtaAdminMap";
import {
  trackMejoresAmigosCtaClick,
  trackMejoresAmigosCtaImpression,
} from "@/lib/mejoresAmigosCtaAnalytics";
import type { BaseTemplateAnalyticsName } from "@/lib/posthogAnalytics";
import {
  getOrCreateTabSessionId,
  POSTHOG_BASE_TEMPLATE_TAB_SESSION_KEY,
  registerBaseTemplateSessionContextPostHog,
  trackBaseTemplateFunnelSessionPostHog,
  trackLightboxClosedPostHog,
  trackLightboxNavigationPostHog,
  trackLightboxOpenedPostHog,
  trackMediaItemClickPostHog,
  trackTemplateUsagePostHog,
  unregisterBaseTemplateSessionContextPostHog,
} from "@/lib/posthogAnalytics";
import { trackViewContent, trackFacebookEvent } from "@/lib/facebookPixel";
import { ChevronLeft, ChevronRight, X } from "lucide-react";
import { useFreshCtaFlags } from "@/hooks/useFreshCtaFlags";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { getMercadoPagoCheckoutVipArUrl } from "@/lib/mercadopagoArCheckoutUrl";
import { useMercadoPagoInstagramEscape } from "@/hooks/useMercadoPagoInstagramEscape";



import { useImageProtection } from "@/hooks/useImageProtection";
import {
  resolveBaseTemplateGalleryPhotos,
  type BaseTemplateGalleryMediaItem,
} from "@/lib/baseTemplateGalleryFromSiteSettings";
import { resolveBaseTemplateSiteContent } from "@/lib/baseTemplateSiteContent";
import {
  getCloudinaryUrl,
  isCloudinaryWatermarkConfigured,
  resolveLogoUrl,
} from "@/lib/cloudinaryUtils";
import {
  getServerTemplateConfig,
  workerCtaNeedsDefensiveFetch,
  workerHtmlHasPositiveHeroCta,
} from "@/lib/serverTemplateDetection";
import { useSiteSettings } from "@/hooks/useSiteSettings";
import { baseTemplateDarkShell } from "@/lib/baseTemplateDarkShell";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { BaseTemplateFooter } from "@/components/templates/BaseTemplateFooter";
import { BaseTemplateTallyContact } from "@/components/templates/BaseTemplateTallyContact";
import {
  BaseTemplateDeferredBannerOverlay,
  BaseTemplateDeferredGallery,
  BaseTemplateHeroCtaOverrideLink,
} from "@/components/templates/BaseTemplate";
import { BaseTemplateHeroCtaMembershipMux } from "@/components/templates/BaseTemplateHeroCtaMembershipMux";
import { resolveHeroCtaForBaseTemplate } from "@/lib/baseTemplateHeroCta";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";


// Type for Cloudflare Worker injected template config
interface TemplateConfig {
  country?: string;
  province?: string;
  city?: string;
  cfCity?: string;
  neighborhood?: string;
  neighborhoodCity?: string;
  templateName?: string;
  language?: string;
  hideCta?: boolean;
  showWishlist?: boolean;
  showCommunity?: boolean;
  showBirthday?: boolean;
  showCheckout?: boolean;
  whatsappBroadcast?: boolean;
  detectionMethod?: string;
  ctaUrl?: string;
  ctaLabel?: string;
}

declare global {
  interface Window {
    __TEMPLATE_CONFIG__?: TemplateConfig;
    Tally?: { loadEmbeds: () => void };
  }
}

export interface TemplateProps {
  /** @deprecated La galería home usa solo site-settings; se mantiene por compatibilidad de páginas demo. */
  mediaItems: Array<{
    id: number;
    type: "video" | "photo";
    src: string;
    title: string;
    playbackId?: string;
    actionButton?: unknown;
  }>;
  /** @deprecated La galería usa solo fotos Cloudinary (site-settings). Ignorado. */
  skipMuxApiFilter?: boolean;
  // Optional: URL for header logo/name link (managed from admin)
  headerUrl?: string;
  // Optional: Logo image URL (Cloudinary, Cloudflare, etc.) - managed from admin
  logoUrl?: string;
  // Optional: Hero title and description (from admin site-settings)
  heroTitle?: string;
  heroDescription?: string;
  // Optional: Banner/hero background image (URL o public_id). Si no se pasa, usa Cloudflare por defecto.
  bannerImageUrl?: string;
  /** Cuando true, no mostrar img del banner hasta tener la URL final (evita flash Cloudflare → site settings) */
  bannerDeferred?: boolean;
  /** Cuando true, no mostrar galería hasta tener datos de site settings (evita carga escalonada) */
  galleryDeferred?: boolean;
  /** Texto "Sobre mí" (página /sobre-mi). Versión corta en home, completa en /sobre-mi */
  sobreMi?: string;
  /** Si es false, oculta el bloque corto en esta vista (p. ej. inicio); /sobre-mi no se ve afectado */
  showSobreMiOnHome?: boolean;
  /** Botón CTA extra en el hero (debajo del broadcast de WhatsApp). Solo se muestra si se pasa. */
  heroCta?: React.ReactNode;
  /** false = ocultar enlace /portfolio en el nav (sin portfolio público). Por defecto true. */
  showPortfolioNavLink?: boolean;
  /** false = ocultar enlace /sobre-mi del footer. Por defecto true. */
  showSobreMi?: boolean;
  /** Nombre de marca (header/footer); desde admin vía Index. */
  brandName?: string;
  /** Tagline del footer; vacío = i18n. */
  footerTagline?: string;
  /**
   * Override del nombre de plantilla para analytics (PostHog/GA/FB).
   * Default: "BaseTemplateBlack". El wrapper internacional pasa
   * "BaseTemplateInternational" para poder filtrar eventos por variante.
   */
  analyticsTemplateName?: BaseTemplateAnalyticsName;
  /**
   * Override de la región reportada en eventos de uso de plantilla.
   * Default: "LATAM". El wrapper internacional pasa la región real
   * (US/SPAIN/LATAM) derivada del país detectado por el Worker.
   */
  analyticsRegion?: string;
  /** false = ocultar sección Mi galería (#portfolio). Por defecto true. */
  showGallerySection?: boolean;
  /** false = ocultar formulario Tally (#contacto). Por defecto true. */
  showTallySection?: boolean;
}

export const BaseTemplateBlack: React.FC<TemplateProps> = ({
  mediaItems: _mediaItems,
  skipMuxApiFilter: _skipMuxApiFilter = true,
  headerUrl = "/",
  logoUrl,
  heroTitle,
  heroDescription,
  bannerImageUrl,
  bannerDeferred = false,
  galleryDeferred = false,
  sobreMi,
  showSobreMiOnHome,
  heroCta,
  showPortfolioNavLink = true,
  showSobreMi = false,
  brandName = BRAND_NAME,
  footerTagline,
  analyticsTemplateName = "BaseTemplateBlack",
  analyticsRegion = "LATAM",
  showGallerySection,
  showTallySection,
}) => {
  const prefersReducedMotion = useReducedMotion();
  const bannerEase = [0.22, 1, 0.36, 1] as const;
  // Worker KV → /api/site-settings → props (Index u otras landings).
  const { settings: siteSettings, isLoading: siteSettingsLoading } = useSiteSettings();
  const site = useMemo(
    () =>
      resolveBaseTemplateSiteContent(siteSettings, siteSettingsLoading, {
        headerUrl,
        logoUrl,
        heroTitle,
        heroDescription,
        bannerImageUrl,
        footerTagline,
        brandName,
        showGallerySection,
        showTallySection,
        showSobreMi,
        sobreMi,
        bannerDeferred,
        galleryDeferred,
      }),
    [
      siteSettings,
      siteSettingsLoading,
      headerUrl,
      logoUrl,
      heroTitle,
      heroDescription,
      bannerImageUrl,
      footerTagline,
      brandName,
      showGallerySection,
      showTallySection,
      showSobreMi,
      sobreMi,
      bannerDeferred,
      galleryDeferred,
    ],
  );

  // Home #portfolio: site-settings curadas; fallback legacy mediaItems en landings demo.
  const activeGalleryMedia = useMemo(
    () =>
      resolveBaseTemplateGalleryPhotos(
        site.gallerySettings,
        _mediaItems,
        { shuffleGallery: true },
      ),
    [
      site.gallerySettings.galleryImageUrls?.join(","),
      site.gallerySettings.alternativeGalleryEnabled,
      site.gallerySettings.alternativeGalleryUrls?.join(","),
      site.gallerySettings.logoUrl,
      _mediaItems,
    ],
  );

  const hasHomeGallery = activeGalleryMedia.length > 0;
  const showHomeGallerySection = site.showGallerySection && hasHomeGallery;

  const showGalleryScrollNav = showHomeGallerySection;
  const showGalleryPortfolioNav =
    (!site.showGallerySection || !hasHomeGallery) && showPortfolioNavLink;
  /** When toggle "Mostrar portfolio en el sitio público" is ON, ensure /portfolio appears in top nav (in addition to home gallery if present). */
  const showPortfolioLinkInNav = showPortfolioNavLink && showGalleryScrollNav;

  const effectiveShowContactNavLink = site.showTallySection;
  const { language } = useLanguage();
  const { t } = useTranslation();
  const templateConfig = useMemo(() => getServerTemplateConfig(), []);
  const countryCode = templateConfig?.country;

  // Tracking de fotos: impresiones del grid (#portfolio) + apertura en lightbox.
  // Alimenta /admin/fotos-analytics (quién vio cada foto, con geo del Worker).
  usePhotoImpressions({
    section: "home_gallery",
    templateType: analyticsTemplateName,
    countryCode,
    ready: activeGalleryMedia,
  });

  const effectiveHeroCta = useMemo(() => {
    if (heroCta) return heroCta;
    const override = resolveHeroCtaForBaseTemplate(
      siteSettings,
      countryCode,
      templateConfig?.heroCta,
    );
    if (!override) return undefined;
    if (override.type === "membership") {
      return (
        <BaseTemplateHeroCtaMembershipMux
          override={override}
          countryCode={countryCode}
          templateName="BaseTemplateBlack"
          workerLanguage={language}
        />
      );
    }
    return (
      <BaseTemplateHeroCtaOverrideLink
        override={override}
        countryCode={countryCode}
        templateName="BaseTemplateBlack"
      />
    );
  }, [heroCta, siteSettings, countryCode, templateConfig?.heroCta, language]);

  useImageProtection();

  // ── CTA hero: site-settings / KV override; si no, flags geo del Worker ──
  const initialHideCta = !!templateConfig?.hideCta;
  const initialShowCheckout = !!templateConfig?.showCheckout;
  const initialShowCommunity = !!templateConfig?.showCommunity;
  const initialShowBirthday = !!templateConfig?.showBirthday;
  const initialShowWishlist = !!templateConfig?.showWishlist;
  const initialShowBestFriends = !!templateConfig?.showBestFriends;
  // Admin override URL / Label desde wishlist-config (admin guarda cta_type=checkout
  // con url+label custom para CABA, GBA, etc.). El Worker propaga ambos.
  const initialCtaUrl = templateConfig?.ctaUrl;
  const initialCtaLabel = templateConfig?.ctaLabel;
  // NOTE: templateConfig.mejoresAmigosTiers también está expuesto por el Worker
  // (para admin tooling), pero el hero render NO lo usa: la URL ganadora ya
  // viene resuelta en `ctaUrl` (admin clickeó 💰 medium o 💎 alto per-geo en
  // /admin/cta-por-region, el tier resuelto se guarda en wishlist_geo_config.url).
  const isArgentina = countryCode === "AR" || countryCode === "ar";
  const mpIgLang = (language?.split("-")[0] as "es" | "en" | "pt") ?? "es";
  const { interceptMercadoPagoCta, overlay: mpIgEscapeOverlay } =
    useMercadoPagoInstagramEscape({
      language: mpIgLang,
      surface: "hero_cta_mejores_amigos",
      countryCode,
    });

  // ── Background refresh para AR (mismo mecanismo que BaseTemplate.tsx) ──────
  // El Worker KV tiene hasta 60s de latencia. Si el admin cambia el CTA
  // (e.g. birthday → community), el HTML puede llegar stale. El fetch fresco
  // corrige sin que el visitor tenga que recargar.
  // Skeleton: cubre tanto "suspicious empty" (sin flag inicial) como "stale flag"
  // (flag inicial distinto al de la DB) — evita switch jarring post-render.
  const mejoresAmigosImpressionSentRef = useRef(false);
  const heroPageEnterSentRef = useRef(false);
  const heroGenericImpressionSentRef = useRef<string | null>(null);

  const isCookieFallback =
    templateConfig?.detectionMethod === "cookies_fallback" &&
    Boolean(countryCode) &&
    countryCode !== "UNKNOWN";
  const isArGeoSession = !effectiveHeroCta && (isArgentina || isCookieFallback);
  const needsDefensiveCtaFetch = workerCtaNeedsDefensiveFetch(templateConfig);

  const { freshFlags, isFreshLoading } = useFreshCtaFlags({
    enabled: isArGeoSession,
    skipInitialFetch: isArGeoSession && !needsDefensiveCtaFetch,
    countryCode,
    templateConfig,
  });

  const hideCta = freshFlags?.hideCta ?? initialHideCta;
  const showCheckout = freshFlags?.showCheckout ?? initialShowCheckout;
  const showBirthday = freshFlags?.showBirthday ?? initialShowBirthday;
  const showWishlist = freshFlags?.showWishlist ?? initialShowWishlist;
  const showBestFriends = freshFlags?.showBestFriends ?? initialShowBestFriends;
  const ctaUrl = freshFlags?.ctaUrl ?? initialCtaUrl;
  const ctaLabel = freshFlags?.ctaLabel ?? initialCtaLabel;
  let showCommunity = freshFlags?.showCommunity ?? initialShowCommunity;

  const showCtaSkeleton =
    isArGeoSession &&
    needsDefensiveCtaFetch &&
    isFreshLoading &&
    !freshFlags &&
    !workerHtmlHasPositiveHeroCta(templateConfig);

  const mejoresAmigosCtaVisible =
    !hideCta &&
    !showCtaSkeleton &&
    !effectiveHeroCta &&
    (showBirthday || showBestFriends) &&
    Boolean(ctaUrl);
  const mejoresAmigosCtaResolved =
    freshFlags?.resolved ??
    (showBestFriends
      ? "mejores_amigos"
      : showBirthday
        ? "birthday"
        : undefined);
  const mejoresAmigosCtaGeoSource = freshFlags?.source;
  const mejoresAmigosTierUrls =
    freshFlags?.mejoresAmigosTiers ?? templateConfig?.mejoresAmigosTiers;

  const withHeroGeo = useCallback(
    (
      heroCtaType: string,
      href?: string,
      extra?: Record<string, string | number | boolean | undefined>,
    ) => ({
      cta_config_source: "cta_por_region",
      ...(freshFlags ? { cta_from_fresh_fetch: true } : {}),
      ...buildHeroCtaSessionContext({
        ctaResolved: mejoresAmigosCtaResolved ?? templateConfig?.heroCtaResolved,
        ctaGeoSource: mejoresAmigosCtaGeoSource ?? templateConfig?.heroCtaSource,
        heroCtaType,
        href,
        isArgentina,
      }),
      ...extra,
    }),
    [
      mejoresAmigosCtaResolved,
      mejoresAmigosCtaGeoSource,
      templateConfig?.heroCtaResolved,
      templateConfig?.heroCtaSource,
      isArgentina,
      freshFlags,
    ],
  );

  useEffect(() => {
    if (!mejoresAmigosCtaVisible || !ctaUrl || mejoresAmigosImpressionSentRef.current) {
      return;
    }
    mejoresAmigosImpressionSentRef.current = true;
    trackMejoresAmigosCtaImpression({
      href: ctaUrl,
      label: t("baseTemplate.heroCtaLabelBestFriends"),
      templateName: analyticsTemplateName,
      countryCode,
      showBestFriends,
      showBirthdayLegacy: showBirthday && !showBestFriends,
      ctaResolved: mejoresAmigosCtaResolved,
      ctaGeoSource: mejoresAmigosCtaGeoSource,
      tierUrls: mejoresAmigosTierUrls,
    });
  }, [
    mejoresAmigosCtaVisible,
    ctaUrl,
    countryCode,
    showBestFriends,
    showBirthday,
    mejoresAmigosCtaResolved,
    mejoresAmigosCtaGeoSource,
    mejoresAmigosTierUrls,
    analyticsTemplateName,
    t,
  ]);

  // Defensa legacy showDual → community; respeta hideCta de /api/cta-flags (cta-por-region).
  if (
    !hideCta &&
    freshFlags?.hideCta !== true &&
    freshFlags?.resolved !== "none" &&
    isArgentina &&
    !showCheckout &&
    !showCommunity &&
    !showBirthday &&
    !showWishlist &&
    !showBestFriends &&
    (templateConfig as any)?.showDual
  ) {
    showCommunity = true;
  }

  // Checkout AR → VITE_MP_CHECKOUT_VIP_AR. Community → /invitacion-premium
  // (VIP CTA de la landing → MP + logo).
  const arVipMpUrl = isArgentina ? getMercadoPagoCheckoutVipArUrl() : "";
  const checkoutRoute = resolveCheckoutHeroHref({
    ctaUrl: showCheckout ? ctaUrl : undefined,
    isArgentina,
    arPrecreatedMpUrl: arVipMpUrl,
  });
  const isExternalCheckout = isExternalHeroCtaHref(checkoutRoute);
  const hasCheckoutRoute = Boolean(checkoutRoute);
  const communityRoute = resolveCommunityHeroHref(ctaUrl);
  const isExternalCommunity = isExternalHeroCtaHref(communityRoute);
  const heroCtaFlagsInput = {
    hideCta,
    showCtaSkeleton,
    hasAdminHeroCta: Boolean(effectiveHeroCta),
    showCheckout,
    hasCheckoutRoute,
    showCommunity,
    showBestFriends,
    showBirthday,
    showWishlist,
    ctaUrl,
  };

  useEffect(() => {
    const activeType = resolveActiveHeroCtaType(heroCtaFlagsInput);
    if (!activeType) {
      uninstallHeroCtaEngagementWatch();
      return;
    }

    const flags = buildHeroCtaFlagsSnapshot({
      hideCta,
      showCheckout,
      showCommunity,
      showBestFriends,
      showBirthday,
      showWishlist,
      hasCheckoutRoute,
      showCtaSkeleton,
      fromFreshFetch: Boolean(freshFlags),
      ctaUrl,
      ctaLabel,
      heroCtaResolved: mejoresAmigosCtaResolved ?? templateConfig?.heroCtaResolved,
      heroCtaSource: mejoresAmigosCtaGeoSource ?? templateConfig?.heroCtaSource,
    });

    if (!heroPageEnterSentRef.current) {
      heroPageEnterSentRef.current = true;
      postHeroCtaSlackFunnel("page_enter_hero_landing", {
        templateName: analyticsTemplateName,
        country: countryCode,
        heroCtaType: activeType,
        ctaResolved: mejoresAmigosCtaResolved,
        ctaGeoSource: mejoresAmigosCtaGeoSource,
        lastStep: "page_enter",
        flags,
        forceRecording: true,
        replayNearNow: true,
        extra: withHeroGeo(activeType ?? "none"),
      });
    }

    installHeroCtaEngagementWatch({
      templateName: analyticsTemplateName,
      country: countryCode,
      heroCtaType: activeType,
      flags,
      ctaResolved: mejoresAmigosCtaResolved,
      ctaGeoSource: mejoresAmigosCtaGeoSource,
    });

    return () => uninstallHeroCtaEngagementWatch();
  }, [
    hideCta,
    showCtaSkeleton,
    effectiveHeroCta,
    showCheckout,
    hasCheckoutRoute,
    showCommunity,
    showBestFriends,
    showBirthday,
    showWishlist,
    countryCode,
    mejoresAmigosCtaResolved,
    mejoresAmigosCtaGeoSource,
    analyticsTemplateName,
    freshFlags,
    ctaUrl,
    ctaLabel,
    templateConfig?.heroCtaResolved,
    templateConfig?.heroCtaSource,
    withHeroGeo,
  ]);

  const checkoutCtaLabel = ctaLabel || t("baseTemplate.black.heroCtaLabelCheckout", {
    defaultValue: "Ser Exclusivo",
  });
  const checkoutCtaAriaLabel = t("baseTemplate.black.heroCtaAriaCheckout", {
    defaultValue: checkoutCtaLabel,
  });
  const checkoutCtaContent = (
    <span className="font-body text-sm sm:text-[15px] font-semibold text-white leading-tight tracking-normal">
      {checkoutCtaLabel}
    </span>
  );
  const checkoutCtaClick = () =>
    trackBaseTemplateClick({
      templateName: analyticsTemplateName,
      elementId: "hero_cta_checkout",
      label: checkoutCtaLabel,
      surface: "hero",
      href: checkoutRoute,
      countryCode,
      extra: withHeroGeo("checkout", checkoutRoute, {
        payment_provider: isArgentina ? "mercadopago" : "stripe",
        checkout_resource: arVipMpUrl && checkoutRoute === arVipMpUrl
          ? "mp_precreated_preapproval_plan"
          : ctaUrl
            ? "admin_cta_url_override"
            : "internal_route",
      }),
    });

  useEffect(() => {
    const activeType = resolveActiveHeroCtaType(heroCtaFlagsInput);
    if (
      !activeType ||
      activeType === "mejores_amigos" ||
      activeType === "admin_override"
    ) {
      return;
    }
    if (heroGenericImpressionSentRef.current === activeType) return;
    heroGenericImpressionSentRef.current = activeType;

    const flags = buildHeroCtaFlagsSnapshot({
      hideCta,
      showCheckout,
      showCommunity,
      showBestFriends,
      showBirthday,
      showWishlist,
      hasCheckoutRoute,
      showCtaSkeleton,
      fromFreshFetch: Boolean(freshFlags),
      ctaUrl,
      ctaLabel,
      heroCtaResolved: mejoresAmigosCtaResolved ?? templateConfig?.heroCtaResolved,
      heroCtaSource: mejoresAmigosCtaGeoSource ?? templateConfig?.heroCtaSource,
    });
    const renderKind =
      activeType === "birthday"
        ? "birthday"
        : activeType === "wishlist"
          ? "wishlist"
          : activeType === "checkout"
            ? "checkout"
            : "community";
    const label = resolveHeroCtaDisplayLabel({ kind: renderKind, t, ctaLabel });
    const href = resolveHeroCtaHrefForKind(renderKind, {
      ctaUrl,
      checkoutRoute,
    });

    postHeroCtaSlackFunnel("hero_cta_impression", {
      templateName: analyticsTemplateName,
      country: countryCode,
      heroCtaType: activeType,
      label,
      href,
      ctaResolved: mejoresAmigosCtaResolved,
      ctaGeoSource: mejoresAmigosCtaGeoSource,
      lastStep: "hero_cta_impression",
      flags,
      replayNearNow: true,
      extra: withHeroGeo(activeType, href, {
        ...(activeType === "checkout"
          ? {
              payment_provider: isArgentina ? "mercadopago" : "stripe",
              checkout_resource:
                arVipMpUrl && checkoutRoute === arVipMpUrl
                  ? "mp_precreated_preapproval_plan"
                  : ctaUrl
                    ? "admin_cta_url_override"
                    : "internal_route",
            }
          : {}),
        ...(activeType === "wishlist" ? { cta_disabled: true, cta_reason: "proximamente" } : {}),
      }),
    });
  }, [
    hideCta,
    showCtaSkeleton,
    effectiveHeroCta,
    showCheckout,
    hasCheckoutRoute,
    showCommunity,
    showBestFriends,
    showBirthday,
    showWishlist,
    countryCode,
    checkoutRoute,
    checkoutCtaLabel,
    ctaUrl,
    ctaLabel,
    freshFlags,
    isArgentina,
    arVipMpUrl,
    mejoresAmigosCtaResolved,
    mejoresAmigosCtaGeoSource,
    analyticsTemplateName,
    templateConfig?.heroCtaResolved,
    templateConfig?.heroCtaSource,
    t,
    withHeroGeo,
  ]);

  const ctaSkeleton = (
    <div
      aria-hidden
      className="inline-flex items-center justify-center gap-3 py-3.5 px-5 max-w-[360px] mx-auto rounded-2xl border border-white/20 bg-white/10 backdrop-blur-2xl animate-pulse w-[260px] h-[56px]"
    >
      <span className="h-2 w-32 rounded-full bg-white/30" />
    </div>
  );

  const internalCta = effectiveHeroCta ?? (
    showCtaSkeleton ? ctaSkeleton :
    hideCta ? null :
    showCheckout && hasCheckoutRoute ? (
      isExternalCheckout ? (
        <a
          href={checkoutRoute}
          onClick={(e) => {
            checkoutCtaClick();
            if (interceptMercadoPagoCta(checkoutRoute)) {
              e.preventDefault();
            }
          }}
          className={`${baseTemplateDarkShell.ctaGlass} group`}
          aria-label={checkoutCtaAriaLabel}
        >
          {checkoutCtaContent}
        </a>
      ) : (
        <Link
          to={checkoutRoute}
          onClick={checkoutCtaClick}
          className={`${baseTemplateDarkShell.ctaGlass} group`}
          aria-label={checkoutCtaAriaLabel}
        >
          {checkoutCtaContent}
        </Link>
      )
    ) : showCommunity ? (
      isExternalCommunity ? (
        <a
          href={communityRoute}
          onClick={() =>
            trackBaseTemplateClick({
              templateName: analyticsTemplateName,
              elementId: "hero_cta_community",
              label: resolveHeroCtaDisplayLabel({ kind: "community", t }),
              surface: "hero",
              href: communityRoute,
              countryCode,
              extra: withHeroGeo("community", communityRoute),
            })
          }
          className={`${baseTemplateDarkShell.ctaGlass} group`}
          aria-label={resolveHeroCtaAriaLabel("community", t)}
        >
          <span className="text-xl sm:text-2xl flex-shrink-0 transition-transform duration-300 group-hover:scale-110" role="img" aria-hidden>
            ✨
          </span>
          <span className="font-body text-sm sm:text-[15px] font-semibold text-white leading-tight text-left tracking-normal">
            {resolveHeroCtaDisplayLabel({ kind: "community", t })}
          </span>
        </a>
      ) : (
        <Link
          to={communityRoute}
          onClick={() =>
            trackBaseTemplateClick({
              templateName: analyticsTemplateName,
              elementId: "hero_cta_community",
              label: resolveHeroCtaDisplayLabel({ kind: "community", t }),
              surface: "hero",
              href: communityRoute,
              countryCode,
              extra: withHeroGeo("community", communityRoute),
            })
          }
          className={`${baseTemplateDarkShell.ctaGlass} group`}
          aria-label={resolveHeroCtaAriaLabel("community", t)}
        >
          <span className="text-xl sm:text-2xl flex-shrink-0 transition-transform duration-300 group-hover:scale-110" role="img" aria-hidden>
            ✨
          </span>
          <span className="font-body text-sm sm:text-[15px] font-semibold text-white leading-tight text-left tracking-normal">
            {resolveHeroCtaDisplayLabel({ kind: "community", t })}
          </span>
        </Link>
      )
    ) : showBirthday && !ctaUrl ? (
      <Link
        to={resolveBirthdayHeroHref()}
        onClick={() =>
          trackBaseTemplateClick({
            templateName: analyticsTemplateName,
            elementId: "hero_cta_birthday",
            label: resolveHeroCtaDisplayLabel({ kind: "birthday", t }),
            surface: "hero",
            href: resolveBirthdayHeroHref(),
            countryCode,
            extra: withHeroGeo("birthday", resolveBirthdayHeroHref()),
          })
        }
        className={`${baseTemplateDarkShell.ctaGlass} group`}
        aria-label={resolveHeroCtaAriaLabel("birthday", t)}
        data-attr="hero_cta_birthday"
      >
        <span className="text-xl sm:text-2xl flex-shrink-0" role="img" aria-hidden>🎂</span>
        <span className="font-body text-sm sm:text-[15px] font-semibold text-white leading-tight text-left tracking-normal">
          {resolveHeroCtaDisplayLabel({ kind: "birthday", t })}
        </span>
      </Link>
    ) : (showBirthday || showBestFriends) && ctaUrl ? (
      // Mejores Amigos — UN botón con label "Mejores Amigos" + logo IG.
      //
      // NOTA OPERADOR (2026-06-20): tanto `showBirthday` como `showBestFriends`
      // rendean este bloque. Razón: cta_type=birthday se remapeó semánticamente
      // a Mejores Amigos (mismo flow MP checkout admin-configurado). Las
      // entries legacy con cta_type='birthday' en wishlist_geo_config siguen
      // sirviéndose pero ahora visualmente convergen con cta_type='mejores_amigos'.
      // El /r/cumple route sigue existiendo para links externos directos.
      //
      // FUENTE ÚNICA DE VERDAD: la URL MP la elige el admin per-geo en
      // /admin/cta-por-region clickeando "💰 Precio Medio" o "💎 Precio Alto"
      // (los URLs viven en mejores_amigos_price_config). El tier seleccionado
      // se guarda como url crudo en wishlist_geo_config.url y viaja vía `ctaUrl`.
      // Si el geo no tiene url propio, el backend cae al tier medium por defecto.
      //
      // El visitor SIEMPRE ve "Mejores Amigos" (i18n heroCtaLabelBestFriends) —
      // NUNCA "Precio Medio"/"Alto" (nombres internos de admin). Por eso ignoramos
      // `ctaLabel` del API.
      (() => {
        const isExternalBestFriends = /^https?:\/\//i.test(ctaUrl);
        const trackClick = () =>
          trackMejoresAmigosCtaClick({
            source: "hero_mejores_amigos_base_template_black",
            templateName: analyticsTemplateName,
            elementId: "hero_cta_mejores_amigos",
            label: t("baseTemplate.heroCtaLabelBestFriends"),
            surface: "hero",
            href: ctaUrl,
            countryCode,
            showBestFriends,
            showBirthdayLegacy: showBirthday && !showBestFriends,
            ctaResolved: mejoresAmigosCtaResolved,
            ctaGeoSource: mejoresAmigosCtaGeoSource,
            tierUrls: mejoresAmigosTierUrls,
          });
        const content = (
          <>
            <img
              src="https://res.cloudinary.com/nm-creators/image/upload/v1781986599/Instagram_logo_2016.svg_l1fahg.png"
              alt="Instagram"
              className="w-7 h-7 flex-shrink-0 rounded-md object-contain"
            />
            <div className="flex flex-col gap-0.5 min-w-0 text-left">
              <span className="font-body text-sm sm:text-[15px] font-semibold text-white leading-tight tracking-normal">
                {t("baseTemplate.heroCtaLabelBestFriends")}
              </span>
              <span className="font-body text-xs sm:text-[13px] font-normal text-white/70 leading-tight">
                {t("baseTemplate.bestfriendInstagram.subtitle")}
              </span>
            </div>
          </>
        );
        return isExternalBestFriends ? (
          <a
            href={ctaUrl}
            onClick={(e) => {
              trackClick();
              if (interceptMercadoPagoCta(ctaUrl)) {
                e.preventDefault();
              }
            }}
            className={baseTemplateDarkShell.ctaGlass}
            aria-label={t("baseTemplate.heroCtaLabelBestFriends")}
            data-attr="hero_cta_mejores_amigos"
          >
            {content}
          </a>
        ) : (
          <Link
            to={ctaUrl}
            onClick={trackClick}
            className={baseTemplateDarkShell.ctaGlass}
            aria-label={t("baseTemplate.heroCtaLabelBestFriends")}
            data-attr="hero_cta_mejores_amigos"
          >
            {content}
          </Link>
        );
      })()
    ) : showWishlist ? (
      <span
        role="link"
        aria-disabled="true"
        title="Próximamente"
        className={`${baseTemplateDarkShell.ctaGlass} opacity-50 cursor-not-allowed pointer-events-none grayscale`}
        aria-label={t("baseTemplate.black.heroCtaAriaWishlist")}
      >
        <span className="text-xl sm:text-2xl flex-shrink-0" role="img" aria-label="Regalo">🎁</span>
        <span className="font-body text-sm sm:text-[15px] font-semibold text-white leading-tight tracking-normal">
          {t("baseTemplate.black.heroCtaLabelWishlist")}
        </span>
      </span>
    ) : null
  );

  const heroTitleForDisplay = site.heroTitle;
  const heroDescriptionForDisplay = site.heroDescription;

  // Helper: resolve media src (Cloudinary public_id → full URL)
  const resolveMediaSrc = (src: string | undefined): string =>
    resolveLogoUrl(src) ?? src?.trim() ?? "";
  const galleryUseWatermark = isCloudinaryWatermarkConfigured();

  const [selectedMedia, setSelectedMedia] =
    useState<BaseTemplateGalleryMediaItem | null>(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Instagram browser optimizations
  const instagramOpts = getInstagramOptimizations();

  const lightboxOpenTimeRef = useRef<number | null>(null);
  const lightboxWasOpenRef = useRef(false);
  const lightboxViewedCountRef = useRef(1);
  // Excellence: restore focus to trigger element when closing lightbox (parity with Portfolio + light BaseTemplate)
  const lastFocusedElementRef = useRef<HTMLElement | null>(null);

  const [failedImages, setFailedImages] = useState<Set<number>>(new Set());
  // Galería #portfolio: cada foto arranca invisible sobre su celda (espacio
  // reservado, sin layout shift) y hace fade-in al cargar — en vez de
  // "inyectarse" de golpe. Ver render de la sección de galería.
  const [loadedImages, setLoadedImages] = useState<Set<number>>(new Set());
  const markGalleryImageLoaded = useCallback((id: number) => {
    setLoadedImages((prev) => (prev.has(id) ? prev : new Set(prev).add(id)));
  }, []);

  // Swipe gesture state for Instagram-like navigation (reel-style gallery)
  const swipeStartRef = useRef<{ x: number; y: number; time: number } | null>(
    null,
  );
  const SWIPE_THRESHOLD = 30; // Reduced threshold for more responsive swipes (reel-style)
  const SWIPE_VELOCITY_THRESHOLD = 0.2; // Reduced velocity threshold for smoother navigation

  // Block body scroll when lightbox is open (especially important for Instagram browser)
  useEffect(() => {
    if (selectedMedia) {
      // Store original values
      const originalOverflow = document.body.style.overflow;
      const originalPosition = document.body.style.position;
      const originalWidth = document.body.style.width;
      const originalHeight = document.body.style.height;

      // Block scroll - especially important in Instagram browser
      if (instagramOpts.isInstagramBrowser) {
        document.body.style.position = "fixed";
        document.body.style.width = "100%";
        document.body.style.height = "100%";
      }
      document.body.style.overflow = "hidden";

      // Cleanup function to restore scroll
      return () => {
        document.body.style.overflow = originalOverflow;
        if (instagramOpts.isInstagramBrowser) {
          document.body.style.position = originalPosition;
          document.body.style.width = originalWidth;
          document.body.style.height = originalHeight;
        }
      };
    }
  }, [selectedMedia, instagramOpts.isInstagramBrowser]);

  useEffect(() => {
    if (!selectedMedia) {
      lightboxWasOpenRef.current = false;
      return;
    }
    lightboxOpenTimeRef.current = Date.now();
    if (!lightboxWasOpenRef.current) {
      lightboxViewedCountRef.current = 1;
      trackLightboxOpenedPostHog(
        selectedMedia.id,
        "gallery_click",
        selectedMedia.type,
        analyticsTemplateName,
        countryCode,
      );
      lightboxWasOpenRef.current = true;
    }
  }, [selectedMedia, countryCode, analyticsTemplateName]);

  const closeLightbox = useCallback(
    (closeKind: "x" | "tap") => {
      setSelectedMedia((current) => {
        if (!current) return null;
        const openedAt = lightboxOpenTimeRef.current;
        const timeSpent = openedAt
          ? Math.max(0, Math.round((Date.now() - openedAt) / 1000))
          : 0;
        trackLightboxClosedPostHog(
          current.id,
          timeSpent,
          lightboxViewedCountRef.current,
          analyticsTemplateName,
          countryCode,
        );
        trackBaseTemplateClick({
          templateName: analyticsTemplateName,
          elementId:
            closeKind === "x" ? "lightbox_close_x" : "lightbox_close_tap",
          label:
            closeKind === "x"
              ? "Lightbox close (X)"
              : "Lightbox close (tap)",
          surface: "lightbox",
          countryCode,
          extra: {
            close_kind: closeKind,
            media_id: String(current.id),
          },
        });
        lightboxOpenTimeRef.current = null;

        // Excellence a11y: restore focus to the thumbnail
        const toFocus = lastFocusedElementRef.current;
        if (toFocus && typeof toFocus.focus === "function") {
          window.setTimeout(() => {
            try { toFocus.focus(); } catch {
              /* no-op */
            }
          }, 0);
        }
        lastFocusedElementRef.current = null;

        return null;
      });
    },
    [countryCode, analyticsTemplateName],
  );

  // Navigation helpers for lightbox
  const navigateToPrevious = () => {
    if (!selectedMedia || activeGalleryMedia.length === 0) return;
    const currentIndex = activeGalleryMedia.findIndex(
      (item) => item.id === selectedMedia.id,
    );
    const prevIndex =
      currentIndex > 0 ? currentIndex - 1 : activeGalleryMedia.length - 1;
    const prevItem = activeGalleryMedia[prevIndex];
    trackLightboxNavigationPostHog(
      "prev",
      selectedMedia.id,
      prevItem.id,
      analyticsTemplateName,
      countryCode,
    );
    lightboxViewedCountRef.current += 1;
    setSelectedMedia(prevItem);
    lightboxOpenTimeRef.current = Date.now();
  };

  const navigateToNext = () => {
    if (!selectedMedia || activeGalleryMedia.length === 0) return;
    const currentIndex = activeGalleryMedia.findIndex(
      (item) => item.id === selectedMedia.id,
    );
    const nextIndex =
      currentIndex < activeGalleryMedia.length - 1 ? currentIndex + 1 : 0;
    const nextItem = activeGalleryMedia[nextIndex];
    trackLightboxNavigationPostHog(
      "next",
      selectedMedia.id,
      nextItem.id,
      analyticsTemplateName,
      countryCode,
    );
    lightboxViewedCountRef.current += 1;
    setSelectedMedia(nextItem);
    lightboxOpenTimeRef.current = Date.now();
  };

  // Gallery + hero keyframes moved to static index.css for better initial loading performance (no runtime style injection).

  // Analytics + Instagram browser optimizations (paridad con BaseTemplate.tsx light)
  useEffect(() => {
    const tabSessionId = getOrCreateTabSessionId(POSTHOG_BASE_TEMPLATE_TAB_SESSION_KEY);
    const featureList = [
      "uniform_gallery",
      "mobile_first_responsive",
      "shuffle_functionality",
      ...(instagramOpts.isInstagramBrowser
        ? ["instagram_browser_optimized"]
        : []),
    ];
    registerBaseTemplateSessionContextPostHog(tabSessionId, {
      templateSurface: analyticsTemplateName,
      countryCode,
      isInstagramInApp: instagramOpts.isInstagramBrowser,
    });
    trackBaseTemplateFunnelSessionPostHog({
      tabSessionId,
      templateSurface: analyticsTemplateName,
      countryCode,
      isInstagramInApp: instagramOpts.isInstagramBrowser,
      features: featureList,
    });

    trackTemplateUsageGA(analyticsTemplateName, analyticsRegion, featureList);
    trackTemplateUsagePostHog(analyticsTemplateName, analyticsRegion, featureList);

    trackViewContent(`${analyticsTemplateName} Page`, "page", {
      template: analyticsTemplateName,
      region: analyticsRegion,
      is_instagram_browser: instagramOpts.isInstagramBrowser,
    });

    applyInstagramBrowserCSS();
    return () => {
      unregisterBaseTemplateSessionContextPostHog();
    };
  }, [
    instagramOpts.isInstagramBrowser,
    analyticsTemplateName,
    analyticsRegion,
    countryCode,
  ]);

  return (
    <div
      className={`${baseTemplateDarkShell.pageRoot} ${instagramOpts.cssClasses.container}`}
    >
      {/* Header - mismos tokens que SiteLayout (modo oscuro) */}
      <header className={baseTemplateDarkShell.header}>
        <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-12 sm:h-16 min-h-[44px]">
            {/* Logo - image from Cloudinary/Cloudflare or text fallback */}
            <div className="flex-shrink-0">
              <a
                href={site.headerUrl}
                onClick={() =>
                  trackBaseTemplateClick({
                    templateName: analyticsTemplateName,
                    elementId: "header_logo",
                    label: "Header logo",
                    surface: "header",
                    href: site.headerUrl,
                    countryCode,
                  })
                }
                className={`${baseTemplateDarkShell.logoLink} inline-flex items-center gap-2.5`}
                aria-label={site.brandName}
              >
                {site.logoDisplaySrc ? (
                  <img
                    src={site.logoDisplaySrc}
                    alt={site.brandName}
                    className="h-8 w-8 sm:h-9 sm:w-9 rounded-full object-cover ring-1 ring-white/20 shrink-0"
                    width={36}
                    height={36}
                    decoding="async"
                    fetchPriority="high"
                  />
                ) : null}
                <span>{site.brandName}</span>
              </a>
            </div>

            {/* Nav + idioma + menú móvil (mismo patrón que SiteLayout) */}
            <div className="flex items-center justify-end gap-2 flex-shrink-0">
            <nav
              className="hidden md:flex space-x-5 sm:space-x-6 mr-2"
              aria-label="Main navigation"
            >
              <a
                href="#home"
                onClick={() =>
                  trackBaseTemplateClick({
                    templateName: analyticsTemplateName,
                    elementId: "nav_desktop_home",
                    label: t("baseTemplate.nav.home"),
                    surface: "nav_desktop",
                    href: "#home",
                    countryCode,
                  })
                }
                className={baseTemplateDarkShell.navDesktopLink}
              >
                {t("baseTemplate.nav.home")}
              </a>
              {showGalleryPortfolioNav && (
                <Link
                  to="/portfolio"
                  onClick={() =>
                    trackBaseTemplateClick({
                      templateName: analyticsTemplateName,
                      elementId: "nav_desktop_gallery_portfolio",
                      label: t("baseTemplate.nav.gallery"),
                      surface: "nav_desktop",
                      href: "/portfolio",
                      countryCode,
                    })
                  }
                  className={baseTemplateDarkShell.navDesktopLink}
                >
                  {t("baseTemplate.nav.gallery")}
                </Link>
              )}
              {showGalleryScrollNav && (
              <a
                href="#portfolio"
                onClick={(e) => {
                  e.preventDefault();
                  trackBaseTemplateClick({
                    templateName: analyticsTemplateName,
                    elementId: "nav_desktop_gallery",
                    label: t("baseTemplate.nav.gallery"),
                    surface: "nav_desktop",
                    href: "#portfolio",
                    countryCode,
                  });
                  document.getElementById("portfolio")?.scrollIntoView({ behavior: "smooth" });
                }}
                className={baseTemplateDarkShell.navDesktopLink}
              >
                {t("baseTemplate.nav.gallery")}
              </a>
              )}
              {showPortfolioLinkInNav && (
                <Link
                  to="/portfolio"
                  onClick={() =>
                    trackBaseTemplateClick({
                      templateName: analyticsTemplateName,
                      elementId: "nav_desktop_portfolio",
                      label: t("baseTemplate.nav.portfolio"),
                      surface: "nav_desktop",
                      href: "/portfolio",
                      countryCode,
                    })
                  }
                  className={baseTemplateDarkShell.navDesktopLink}
                >
                  {t("baseTemplate.nav.portfolio")}
                </Link>
              )}
              {effectiveShowContactNavLink && (
              <a
                href="#contacto"
                onClick={() =>
                  trackBaseTemplateClick({
                    templateName: analyticsTemplateName,
                    elementId: "nav_desktop_contacto",
                    label: t("baseTemplate.nav.contact"),
                    surface: "nav_desktop",
                    href: "#contacto",
                    countryCode,
                  })
                }
                className={baseTemplateDarkShell.navDesktopLink}
              >
                {t("baseTemplate.nav.contact")}
              </a>
              )}
            </nav>
            <LanguageSwitcher variant="dark" />
            <div className="md:hidden">
              <button
                type="button"
                aria-label="Toggle navigation menu"
                aria-expanded={mobileMenuOpen}
                onClick={() => {
                  const newState = !mobileMenuOpen;
                  setMobileMenuOpen(newState);
                  trackBaseTemplateClick({
                    templateName: analyticsTemplateName,
                    elementId: "mobile_menu_toggle",
                    label: newState ? "Open mobile menu" : "Close mobile menu",
                    surface: "mobile_menu",
                    countryCode,
                    extra: { menu_open: newState },
                  });
                }}
                className={baseTemplateDarkShell.menuButton}
              >
                <svg
                  className="w-6 h-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 6h16M4 12h16M4 18h16"
                  />
                </svg>
              </button>
            </div>
            </div>
          </div>
        </div>

        {/* Mobile menu */}
        {mobileMenuOpen && (
          <div className={baseTemplateDarkShell.mobileMenuPanel}>
            <div className="px-2 pt-2 pb-3 space-y-1">
              <a
                href="#home"
                onClick={() =>
                  trackBaseTemplateClick({
                    templateName: analyticsTemplateName,
                    elementId: "nav_mobile_home",
                    label: t("baseTemplate.nav.home"),
                    surface: "nav_mobile",
                    href: "#home",
                    countryCode,
                  })
                }
                className={baseTemplateDarkShell.mobileNavLink}
              >
                {t("baseTemplate.nav.home")}
              </a>
              {showGalleryPortfolioNav && (
                <Link
                  to="/portfolio"
                  onClick={() => {
                    trackBaseTemplateClick({
                      templateName: analyticsTemplateName,
                      elementId: "nav_mobile_gallery_portfolio",
                      label: t("baseTemplate.nav.gallery"),
                      surface: "nav_mobile",
                      href: "/portfolio",
                      countryCode,
                    });
                    setMobileMenuOpen(false);
                  }}
                  className={baseTemplateDarkShell.mobileNavLink}
                >
                  {t("baseTemplate.nav.gallery")}
                </Link>
              )}
              {showGalleryScrollNav && (
              <a
                href="#portfolio"
                onClick={(e) => {
                  e.preventDefault();
                  trackBaseTemplateClick({
                    templateName: analyticsTemplateName,
                    elementId: "nav_mobile_gallery",
                    label: t("baseTemplate.nav.gallery"),
                    surface: "nav_mobile",
                    href: "#portfolio",
                    countryCode,
                  });
                  document.getElementById("portfolio")?.scrollIntoView({ behavior: "smooth" });
                  setMobileMenuOpen(false);
                }}
                className={baseTemplateDarkShell.mobileNavLink}
              >
                {t("baseTemplate.nav.gallery")}
              </a>
              )}
              {showPortfolioLinkInNav && (
                <Link
                  to="/portfolio"
                  onClick={() => {
                    trackBaseTemplateClick({
                      templateName: analyticsTemplateName,
                      elementId: "nav_mobile_portfolio",
                      label: t("baseTemplate.nav.portfolio"),
                      surface: "nav_mobile",
                      href: "/portfolio",
                      countryCode,
                    });
                    setMobileMenuOpen(false);
                  }}
                  className={baseTemplateDarkShell.mobileNavLink}
                >
                  {t("baseTemplate.nav.portfolio")}
                </Link>
              )}
              {effectiveShowContactNavLink && (
              <a
                href="#contacto"
                onClick={() => {
                  trackBaseTemplateClick({
                    templateName: analyticsTemplateName,
                    elementId: "nav_mobile_contacto",
                    label: t("baseTemplate.nav.contact"),
                    surface: "nav_mobile",
                    href: "#contacto",
                    countryCode,
                  });
                  setMobileMenuOpen(false);
                }}
                className={baseTemplateDarkShell.mobileNavLink}
              >
                {t("baseTemplate.nav.contact")}
              </a>
              )}
            </div>
          </div>
        )}
      </header>

      {/* Main Content */}
      <main id="main-content" className="relative">
        {/* Black Premium Hero Section - Mobile first */}
        <section
          id="home"
          data-section-name="Hero"
          className="relative w-full bg-black overflow-hidden min-h-[70vh] sm:min-h-[75vh] lg:min-h-[85vh]"
        >
          {/* Banner Image with dark overlay */}
          <div className="absolute inset-0">
            <AnimatePresence mode="wait">
              {!site.bannerDeferred && site.bannerDisplaySrc ? (
                <motion.img
                  key={site.bannerDisplaySrc}
                  src={site.bannerDisplaySrc}
                  srcSet={site.bannerSrcSet}
                  alt={`${BRAND_PERSON_NAME} - Professional Content Creator and Lifestyle Influencer Banner Image`}
                  className="banner-image w-full h-full object-cover opacity-60"
                  style={{ objectPosition: "center center" }}
                  loading="eager"
                  fetchPriority="high"
                  initial={prefersReducedMotion ? false : { opacity: 0 }}
                  animate={{ opacity: 0.6 }}
                  exit={prefersReducedMotion ? { opacity: 0 } : { opacity: 0 }}
                  transition={{ duration: prefersReducedMotion ? 0 : 0.7, ease: bannerEase }}
                  onLoad={() => {
                    const fallbackBg = document.querySelector(".banner-fallback-bg");
                    if (fallbackBg) {
                      (fallbackBg as HTMLElement).style.opacity = "0";
                    }
                  }}
                  onError={(e) => {
                    const target = e.target as HTMLElement;
                    target.style.display = "none";

                    const fallbackBg = document.querySelector(".banner-fallback-bg");
                    if (fallbackBg) {
                      (fallbackBg as HTMLElement).style.opacity = "1";
                    }

                    if (typeof window !== "undefined" && window.gtag) {
                      window.gtag("event", "image_load_error", {
                        event_category: "Cloudflare Images",
                        event_label: "banner_image_failed",
                        image_id: "9fdc1148-5ac5-48d3-2a3a-6ce149481300",
                        template: "BaseTemplate",
                        has_env_var: !!import.meta.env.VITE_CLOUDFLARE_ACCOUNT_HASH,
                      });
                    }
                  }}
                />
              ) : null}
            </AnimatePresence>
            {site.bannerDeferred ? (
              <BaseTemplateDeferredBannerOverlay
                tone="dark"
                brandName={site.brandName}
                logoUrl={site.logoUrl}
                prefersReducedMotion={prefersReducedMotion}
              />
            ) : null}
          </div>

          {/* Premium Black Gradient Fallback */}
          <div
            className={`banner-fallback-bg absolute inset-0 transition-opacity duration-700 ${site.bannerDeferred || !site.bannerRaw ? "opacity-100" : "opacity-0"}`}
            style={{
              background: `
              radial-gradient(ellipse 100% 60% at 50% -10%, rgba(40, 40, 42, 0.6) 0%, transparent 60%),
              radial-gradient(ellipse 90% 50% at 0% 100%, rgba(30, 30, 32, 0.5) 0%, transparent 50%),
              radial-gradient(ellipse 90% 50% at 100% 80%, rgba(35, 35, 37, 0.45) 0%, transparent 50%),
              linear-gradient(180deg, #0a0a0a 0%, #111111 50%, #0a0a0a 100%)
            `,
            }}
          />

          {/* Dark vignette overlay */}
          <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-transparent to-black/60" />

          {/* Premium Grain Texture */}
          <div
            className="absolute inset-0 opacity-[0.03] mix-blend-overlay"
            style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 400 400' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='6.5' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E")`,
            }}
          />

          {/* Hero Content - Mobile first with smooth animations */}
          <div className="relative min-h-[70vh] sm:min-h-[75vh] lg:min-h-[85vh] flex items-center justify-center px-4 py-20 sm:px-6 sm:py-24 lg:px-8 lg:py-28">
            <div className="max-w-5xl mx-auto text-center space-y-6 sm:space-y-8 lg:space-y-10">
              <div className="space-y-4 sm:space-y-5 md:space-y-6">
                {heroTitleForDisplay?.trim() && (() => {
                  const raw = heroTitleForDisplay.trim();
                  const parts = raw.split(/\s*[\u007C\uFF5C\u00A6]\s*/);
                  const name = parts[0]?.trim() || raw;
                  const subtitle = parts.length >= 2 ? parts.slice(1).join(" ").trim() : "";
                  return (
                    <>
                      <h1 
                        className="text-3xl sm:text-5xl md:text-6xl lg:text-7xl xl:text-8xl font-display font-bold text-white tracking-tighter leading-[1.1] drop-shadow-2xl"
                        style={{ 
                          animation: "heroFadeInUp 0.8s cubic-bezier(0.16, 1, 0.3, 1) both",
                          animationDelay: "0.1s"
                        }}
                      >
                        {name}
                      </h1>
                      {subtitle && (
                        <p 
                          className="font-body text-base sm:text-xl md:text-2xl lg:text-3xl text-white/90 font-light tracking-normal leading-relaxed drop-shadow-xl"
                          style={{ 
                            animation: "heroFadeInUp 0.8s cubic-bezier(0.16, 1, 0.3, 1) both",
                            animationDelay: "0.3s"
                          }}
                        >
                          {subtitle}
                        </p>
                      )}
                    </>
                  );
                })()}
                {heroDescriptionForDisplay?.trim() && (
                  <p 
                    className="font-body text-sm sm:text-base md:text-lg lg:text-xl text-white/75 max-w-3xl mx-auto leading-relaxed font-light tracking-normal px-2 sm:px-4 drop-shadow-lg"
                    style={{ 
                      animation: "heroFadeInUp 0.8s cubic-bezier(0.16, 1, 0.3, 1) both",
                      animationDelay: "0.5s"
                    }}
                  >
                    {heroDescriptionForDisplay.trim()}
                  </p>
                )}
              </div>

              {/* CTA hero: Worker (__TEMPLATE_CONFIG__) — comunidad / cumple / wishlist; heroCta override */}
              {internalCta && (
                <div
                  style={{ 
                    animation: "ctaFadeInScale 0.8s cubic-bezier(0.16, 1, 0.3, 1) both",
                    animationDelay: "0.9s"
                  }}
                >
                  {internalCta}
                </div>
              )}

              {/* Social Links - Removed (Instagram and YouTube) */}
            </div>
          </div>
        </section>

        {/* Sobre mí - versión corta, link a /sobre-mi para el texto completo */}
        {showSobreMiOnHome !== false && site.sobreMi?.trim() && (
          <section
            id="sobre-mi-preview"
            className="py-12 sm:py-16 md:py-20 px-4 sm:px-6 lg:px-8 bg-zinc-950 border-y border-white/5"
          >
            <div className="max-w-3xl mx-auto text-center">
              <h2 className="text-xl sm:text-2xl md:text-3xl font-display font-bold text-white mb-6 tracking-tight">
                {t("baseTemplate.about.sectionTitle")}
              </h2>
              <p className="font-body text-white/70 text-sm sm:text-base md:text-lg leading-relaxed mb-6 font-light tracking-normal">
                {site.sobreMi.length > 180
                  ? `${site.sobreMi.slice(0, 180).trim()}…`
                  : site.sobreMi}
              </p>
              <Link
                to="/sobre-mi"
                onClick={() =>
                  trackBaseTemplateClick({
                    templateName: analyticsTemplateName,
                    elementId: "sobre_mi_read_more",
                    label: t("baseTemplate.about.readMore"),
                    surface: "sobre_mi",
                    href: "/sobre-mi",
                    countryCode,
                  })
                }
                className="inline-flex items-center gap-2 text-sm font-medium text-white/80 hover:text-white ui-transition-colors duration-200 group"
              >
                {t("baseTemplate.about.readMore")}
                <svg className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </Link>
            </div>
          </section>
        )}

        {/* Portfolio/Gallery Section - Black Premium style */}
        {showHomeGallerySection && (
        <section
          id="portfolio"
          data-section-name="Gallery"
          className="py-12 px-2 sm:py-16 sm:px-4 md:py-20 md:px-6 lg:py-24 lg:px-8 bg-black"
        >
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-8 sm:mb-12 md:mb-16">
              <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-display font-bold text-white mb-3 sm:mb-4 tracking-tight">
                {t("baseTemplate.gallery.title")}
              </h2>
            </div>

            {/* Gallery: uniform grid - formal, consistent layout. No mostrar hasta tener site settings. */}
            <AnimatePresence mode="wait">
            {site.galleryDeferred ? (
              <motion.div
                key="gallery-loader"
                initial={prefersReducedMotion ? false : { opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={prefersReducedMotion ? { opacity: 0 } : { opacity: 0, y: -8 }}
                transition={{ duration: prefersReducedMotion ? 0 : 0.3, ease: bannerEase }}
              >
                <BaseTemplateDeferredGallery
                  tone="dark"
                  brandName={site.brandName}
                  logoUrl={site.logoUrl}
                  label={t("baseTemplate.gallery.loading")}
                  prefersReducedMotion={prefersReducedMotion}
                />
              </motion.div>
            ) : (() => {
              const displayMedia = activeGalleryMedia
                .filter((item) => !failedImages.has(item.id))
                .slice(0, 9);
              return (
            <motion.div
              key="gallery-content"
              className={`gallery-grid-uniform mb-8 sm:mb-16 ${instagramOpts.cssClasses.gallery}`}
              initial={prefersReducedMotion ? false : { opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={prefersReducedMotion ? { opacity: 0 } : { opacity: 0, y: 12 }}
              transition={{ duration: prefersReducedMotion ? 0 : 0.35, ease: bannerEase }}
            >
              {displayMedia.map((item, index) => (
                  <div
                    key={item.id}
                    data-photo-track
                    data-photo-src={resolveMediaSrc(item.src)}
                    data-photo-position={index}
                    className={`gallery-grid-item relative bg-zinc-900 overflow-hidden touch-manipulation cursor-pointer ${index === 8 ? "hidden sm:block" : ""}`}
                  >
                    {/* Simple click to open - No hover arrows, super user friendly */}
                    <button
                      type="button"
                      onClick={() => {
                        trackMediaItemClickPostHog(
                          item.id,
                          item.type,
                          item.title,
                          index,
                          analyticsTemplateName,
                          countryCode,
                        );
                        trackPhotoOpen(resolveMediaSrc(item.src), index, {
                          section: "home_gallery",
                          templateType: analyticsTemplateName,
                          countryCode,
                        });
                        lastFocusedElementRef.current = document.activeElement as HTMLElement | null;
                        setSelectedMedia(item);
                      }}
                      aria-label={t("baseTemplate.galleryAlt.viewAria", {
                        type:
                          item.title ||
                          t("baseTemplate.galleryAlt.contentFallback"),
                      })}
                      className="w-full h-full focus:outline-none focus:ring-2 focus:ring-foreground/20 active:opacity-90"
                    >
                      <img
                        src={
                          resolveMediaSrc(item.src).includes("res.cloudinary.com")
                            ? getCloudinaryUrl(resolveMediaSrc(item.src), { watermark: galleryUseWatermark })
                            : item.src
                        }
                        srcSet={
                          resolveMediaSrc(item.src).includes("res.cloudinary.com")
                            ? `${getCloudinaryUrl(resolveMediaSrc(item.src), { width: 400, watermark: galleryUseWatermark })} 400w, ${getCloudinaryUrl(resolveMediaSrc(item.src), { width: 800, watermark: galleryUseWatermark })} 800w, ${getCloudinaryUrl(resolveMediaSrc(item.src), { width: 1200, watermark: galleryUseWatermark })} 1200w`
                            : undefined
                        }
                        sizes={
                          resolveMediaSrc(item.src).includes("res.cloudinary.com")
                            ? "(max-width: 639px) 50vw, (max-width: 1023px) 33vw, (max-width: 1535px) 25vw, 20vw"
                            : undefined
                        }
                        alt={`${item.title ? `${item.title} — ` : ""}${t("baseTemplate.galleryAlt.photoSuffix")}`}
                        className={`w-full h-full object-cover transition-opacity ease-out ${prefersReducedMotion ? "duration-0" : "duration-700"} ${loadedImages.has(item.id) ? "opacity-100" : "opacity-0"}`}
                        // Primeras 2 fotos eager+prioridad: acortan la ventana en que
                        // la galería aparece "vacía". El resto lazy (perf below-fold).
                        loading={index < 2 ? "eager" : "lazy"}
                        fetchPriority={index < 2 ? "high" : "auto"}
                        decoding="async"
                        onLoad={() => markGalleryImageLoaded(item.id)}
                        onError={(e) => {
                          // Hide broken images silently - don't show in gallery
                          setFailedImages((prev) => new Set(prev).add(item.id));
                          const target = e.target as HTMLImageElement;
                          if (target) {
                            target.style.display = "none";
                          }
                        }}
                      />
                    </button>
                  </div>
                ))}
            </motion.div>
              );
            })()}
            </AnimatePresence>

            {/* CTA buttons removed - BaseTemplate should only have gallery and social links */}
          </div>
        </section>
        )}

        {/* Contact — Tally @ consulta.delfuocampo.com → webhook /api/tally-webhook */}
        {site.showTallySection && (
          <BaseTemplateTallyContact
            countryCode={countryCode}
            templateName={analyticsTemplateName}
            variant="dark"
          />
        )}
      </main>

      <BaseTemplateFooter
        variant="dark"
        language={language}
        contactHref={effectiveShowContactNavLink ? "#contacto" : undefined}
        analyticsTemplate={analyticsTemplateName}
        countryCode={countryCode}
        brandName={site.brandName}
        footerTagline={site.footerTagline}
        // Edad desde site-settings inyectado por el Worker (detección detallada geo + brandAge para footer).
        // No se muestra en hero. El worker es clave para first-paint correcto con los templates.
        brandAge={(site.brandAge || "").trim()}
        showPortfolioNavLink={showPortfolioNavLink}
        showSobreMiNavLink={site.showSobreMi}
      />

      {/* Reel-style Lightbox Modal - Premium Black Style */}
      {selectedMedia && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label={t("baseTemplate.lightbox.ariaLabel", { defaultValue: "Galería" })}
          className={`fixed inset-0 z-50 bg-black ${
            instagramOpts.cssClasses.modal || ""
          }`}
          style={{
            touchAction: "none",
          }}
          onClick={(e) => {
            // Excellence: close on backdrop click
            if (e.target === e.currentTarget) {
              closeLightbox("tap");
            }
          }}
        >
          {/* Close Button - Safe area aware */}
          <button
            type="button"
            onClick={() => closeLightbox("x")}
            className="absolute w-11 h-11 sm:w-12 sm:h-12 flex items-center justify-center text-white/90 hover:text-white bg-white/10 rounded-full hover:bg-white/20 backdrop-blur-xl ui-transition duration-200 z-30 focus:outline-none focus:ring-2 focus:ring-white/30 touch-manipulation active:scale-95"
            style={{
              top: "max(1rem, env(safe-area-inset-top, 0px))",
              right: "max(1rem, env(safe-area-inset-right, 0px))",
            }}
            aria-label={t("baseTemplate.lightbox.close")}
          >
            <X className="w-6 h-6" strokeWidth={2.5} />
          </button>

          {/* Media counter indicator */}
          <div
            className="absolute left-1/2 -translate-x-1/2 z-30 px-4 py-2 bg-white/10 backdrop-blur-xl rounded-full text-white/90 text-xs font-medium tracking-wider"
            style={{
              top: "max(1rem, env(safe-area-inset-top, 0px))",
            }}
          >
            {activeGalleryMedia.findIndex((item) => item.id === selectedMedia.id) +
              1}{" "}
            /{" "}
            {activeGalleryMedia.filter((item) => !failedImages.has(item.id)).length}
          </div>

          {/* Navigation buttons - visibles en mobile y desktop */}
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              trackBaseTemplateClick({
                templateName: analyticsTemplateName,
                elementId: "lightbox_nav_prev",
                label: "Lightbox previous",
                surface: "lightbox",
                countryCode,
              });
              navigateToPrevious();
            }}
            className="absolute left-2 sm:left-4 top-1/2 -translate-y-1/2 w-11 h-11 sm:w-12 sm:h-12 flex items-center justify-center text-white/70 hover:text-white bg-white/10 rounded-full hover:bg-white/20 backdrop-blur-xl ui-transition duration-200 z-20 focus:outline-none focus:ring-2 focus:ring-white/20 touch-manipulation active:scale-95"
            aria-label={t("baseTemplate.lightbox.previous")}
          >
            <ChevronLeft className="w-6 h-6" strokeWidth={2} />
          </button>

          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              trackBaseTemplateClick({
                templateName: analyticsTemplateName,
                elementId: "lightbox_nav_next",
                label: "Lightbox next",
                surface: "lightbox",
                countryCode,
              });
              navigateToNext();
            }}
            className="absolute right-2 sm:right-4 top-1/2 -translate-y-1/2 w-11 h-11 sm:w-12 sm:h-12 flex items-center justify-center text-white/70 hover:text-white bg-white/10 rounded-full hover:bg-white/20 backdrop-blur-xl ui-transition duration-200 z-20 focus:outline-none focus:ring-2 focus:ring-white/20 touch-manipulation active:scale-95"
            aria-label={t("baseTemplate.lightbox.next")}
          >
            <ChevronRight className="w-6 h-6" strokeWidth={2} />
          </button>

          {/* Reel Content - Full screen swipeable */}
          <div
            className="relative w-full h-full flex items-center justify-center overflow-hidden bg-black"
            style={{
              height: "100dvh",
              WebkitOverflowScrolling: "touch",
            }}
            onTouchStart={(e) => {
              const target = e.target as HTMLElement;
              if (
                target.closest("mux-player") ||
                target.closest(".action-button-container")
              ) {
                return;
              }
              const touch = e.touches[0];
              swipeStartRef.current = {
                x: touch.clientX,
                y: touch.clientY,
                time: Date.now(),
              };
            }}
            onTouchMove={(e) => {
              if (swipeStartRef.current) {
                const touch = e.touches[0];
                const deltaX = Math.abs(
                  touch.clientX - swipeStartRef.current.x,
                );
                const deltaY = Math.abs(
                  touch.clientY - swipeStartRef.current.y,
                );
                // Prevent default to allow smooth swipe animation
                if (deltaX > 10 || deltaY > 10) {
                  e.preventDefault();
                }
              }
            }}
            onTouchEnd={(e) => {
              if (!swipeStartRef.current) return;

              const target = e.target as HTMLElement;
              if (
                target.closest("mux-player") ||
                target.closest(".action-button-container")
              ) {
                swipeStartRef.current = null;
                return;
              }

              const touch = e.changedTouches[0];
              const deltaX = touch.clientX - swipeStartRef.current.x;
              const deltaY = touch.clientY - swipeStartRef.current.y;
              const deltaTime = Date.now() - swipeStartRef.current.time;
              const velocityX = Math.abs(deltaX) / deltaTime;
              const velocityY = Math.abs(deltaY) / deltaTime;

              const swipeThreshold = instagramOpts.isInstagramBrowser ? 20 : 30;
              const velocityThreshold = 0.15;

              // Vertical swipe - Instagram reel style (up = next, down = previous)
              if (
                Math.abs(deltaY) > Math.abs(deltaX) &&
                Math.abs(deltaY) > swipeThreshold &&
                velocityY > velocityThreshold
              ) {
                if (deltaY < 0) {
                  // Swipe up - next item
                  navigateToNext();
                } else {
                  // Swipe down - previous item
                  navigateToPrevious();
                }
              }
              // Horizontal swipe - alternative navigation
              else if (
                Math.abs(deltaX) > Math.abs(deltaY) &&
                Math.abs(deltaX) > swipeThreshold &&
                velocityX > velocityThreshold
              ) {
                if (deltaX > 0) {
                  navigateToPrevious();
                } else {
                  navigateToNext();
                }
              }
              // Tap to close (if minimal movement)
              else if (Math.abs(deltaX) < 10 && Math.abs(deltaY) < 10) {
                closeLightbox("tap");
              }

              swipeStartRef.current = null;
            }}
          >
            {/* Animated content wrapper */}
            <div
              className="w-full h-full flex items-center justify-center transition-transform duration-300 ease-out"
              key={selectedMedia.id}
              style={{
                animation: "fadeSlideIn 0.25s ease-out",
              }}
            >
              <div className="relative w-full h-full flex items-center justify-center bg-black px-2">
                <img
                  key={selectedMedia.id}
                  src={
                    resolveMediaSrc(selectedMedia.src).includes("res.cloudinary.com")
                      ? getCloudinaryUrl(resolveMediaSrc(selectedMedia.src), {
                          watermark: galleryUseWatermark,
                        })
                      : selectedMedia.src
                  }
                  alt={`${selectedMedia.title ? `${selectedMedia.title} — ` : ""}${t("baseTemplate.galleryAlt.photoSuffix")}`}
                  className="max-w-full max-h-full object-contain rounded-lg shadow-[0_0_80px_rgba(0,0,0,0.9)]"
                  style={{
                    maxHeight: "85vh",
                    animation: "scaleIn 0.3s ease-out",
                  }}
                />
              </div>
            </div>

            {/* Hint mobile: desliza o flechas para cambiar, toca para cerrar */}
            <div
              className="absolute bottom-6 left-1/2 -translate-x-1/2 flex flex-col items-center gap-1 text-white/40 pointer-events-none md:hidden"
              style={{
                paddingBottom: "env(safe-area-inset-bottom, 0px)",
              }}
            >
              <span className="text-xs tracking-wider font-light">
                {t("baseTemplate.lightbox.swipeHint")}
              </span>
            </div>
          </div>

          {/* CSS Animation keyframes - smooth and elegant */}
          <style>{`
            @keyframes fadeSlideIn {
              from {
                opacity: 0;
                transform: translateY(30px);
              }
              to {
                opacity: 1;
                transform: translateY(0);
              }
            }
            @keyframes scaleIn {
              from {
                opacity: 0;
                transform: scale(0.92);
              }
              to {
                opacity: 1;
                transform: scale(1);
              }
            }
          `}</style>
        </div>
      )}

      {mpIgEscapeOverlay}
    </div>
  );
};
